import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExtractionWarmup } from "../useExtractionWarmup";
import { useFormDialog } from "../../../components/primitives/feedback/FormDialog/useFormDialog";
import type { BatchJobKind } from "../../../lib/corpusApi";
import type { BatchJobParamsForm } from "./batch_job_kind_contract";
import {
  useAdmissionRecheckParams,
  type UseAdmissionRecheckParamsResult,
} from "./kinds/admission_recheck";
import { useDocumentSectionsRefreshParams } from "./kinds/document_sections_refresh";
import {
  useFieldReannotationParams,
  type UseFieldReannotationParamsResult,
} from "./kinds/field_reannotation";
import { usePubmedIngestParams, type UsePubmedIngestParamsResult } from "./kinds/pubmed_ingest";
import {
  useDocumentIndexingRefreshParams,
  type UseDocumentIndexingRefreshParamsResult,
} from "./kinds/document_indexing_refresh";
import { ROUTES } from "../../../route_paths";

export interface UseNewBatchJobDialogOptions {
  /** Whether the dialog is showing — what it checks the extraction service for is only worth
   *  waking while someone might use it. */
  open: boolean;
  onClose: () => void;
  /** Called once the job is created (before navigating away) — typically refreshes the job list
   *  in the background so it's already up to date when the user navigates back to it. */
  onCreated: () => void;
}

export interface UseNewBatchJobDialogResult {
  kind: BatchJobKind;
  setKind: (kind: BatchJobKind) => void;
  /** Each kind's params form. All are kept alive, so switching kind does not lose what was typed. */
  admissionRecheck: UseAdmissionRecheckParamsResult;
  pubmedIngest: UsePubmedIngestParamsResult;
  fieldReannotation: UseFieldReannotationParamsResult;
  documentIndexingRefresh: UseDocumentIndexingRefreshParamsResult;
  /** What the person typed as the job's name; blank leaves it to the server's default. */
  name: string;
  setName: (name: string) => void;
  /** Whether the chosen kind's params are enough to create the job. */
  canCreate: boolean;
  dialogProps: ReturnType<typeof useFormDialog>["dialogProps"];
}

/** Collects a batch job kind and that kind's params, creates the job, and navigates to its detail
 *  page on success. */
export function useNewBatchJobDialog({
  open,
  onClose,
  onCreated,
}: UseNewBatchJobDialogOptions): UseNewBatchJobDialogResult {
  const navigate = useNavigate();
  const [kind, setKind] = useState<BatchJobKind>("admission_recheck");
  const [name, setName] = useState("");
  const admissionRecheck = useAdmissionRecheckParams();
  // Validating a PubMed query goes through the extraction service, which can be scaled to zero:
  // wake it once the dialog is open on that kind, and keep the kind unavailable until it is up.
  const { warm: extractionWarm } = useExtractionWarmup({
    enabled: open && kind === "pubmed_ingest",
  });
  const pubmedIngest = usePubmedIngestParams({ extractionWarm });
  const fieldReannotation = useFieldReannotationParams();
  const documentIndexingRefresh = useDocumentIndexingRefreshParams();
  const documentSectionsRefresh = useDocumentSectionsRefreshParams();

  const forms: Record<BatchJobKind, BatchJobParamsForm> = {
    admission_recheck: admissionRecheck,
    pubmed_ingest: pubmedIngest,
    field_reannotation: fieldReannotation,
    document_indexing_refresh: documentIndexingRefresh,
    document_sections_refresh: documentSectionsRefresh,
    // Started by closing a coverage gap as filled, not from here.
    coverage_gap_recheck: {
      canStart: false,
      start: () => Promise.reject(new Error("Close the coverage gap as filled to start this")),
    },
  };
  const form = forms[kind];

  const { dialogProps } = useFormDialog({
    onSubmit: async () => {
      const job = await form.start(name.trim() || undefined);
      setName("");
      onCreated();
      navigate(ROUTES.internal.batchJobs.detail.path({ id: job.id }));
    },
    onClose,
  });

  return {
    kind,
    setKind,
    admissionRecheck,
    pubmedIngest,
    fieldReannotation,
    documentIndexingRefresh,
    name,
    setName,
    canCreate: form.canStart,
    dialogProps,
  };
}
